import * as React from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material";

const UITextField = (props) => {
  var {
    name,
    value,
    tooltip,
    prefix,
    error,
    title,
    placeholder,
    onChange,
    onBlur,
    onKeyDown = null,
    variant = "outlined",
    size = "small",
    type = "text",
    maxLength = "",
    multiline,
    rows,
    sx = {},
    disabled = false,
    maxRows = 5,
    autoFocus = false,
    postfix = "",
    inputProps = {},
    inputprops = {},
    className = "",
    inputPropsStyles = {},
    readonly = false,
    fullWidth = true,
    inputRef,
    showErrorText = true,
    helperTextStyles = {},
    errorPositionAbsolute = false,
    required = false,
    InputLabelProps = {},
    allowNegative = true,
  } = props;

  const handleOnBlur = (e) => {
    const el = {
      ...e,
      target: {
        ...e.target,
        name,
        // value: type === "number" ? (value == "" ? value : +value) : value,
      }, // converting empty string into interger is equal to 0 ( +"" = 0), field not going to be empty.
    };
    if (onBlur) onBlur(el);
  };

  const handleKeyDown = (e) => {
    if (type === "number") {
      // Allow Ctrl/Cmd combos: C (copy), V (paste), X (cut), A (select all)
      if (
        (e.ctrlKey || e.metaKey) &&
        ["a", "c", "v", "x"].includes(e.key.toLowerCase())
      ) {
        return;
      }

      // Allow minus sign only at the start of the field (and only when allowed)
      if (e.key === "-") {
        if (!allowNegative || e.target.selectionStart !== 0) {
          e.preventDefault();
        }
        return;
      }

      // Allow only numbers, decimal point, backspace, delete, tab, escape, enter, and arrow keys
      if (
        !(
          (e.key >= "0" && e.key <= "9") ||
          e.key === "." ||
          e.key === "Backspace" ||
          e.key === "Tab" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight" ||
          e.key === "ArrowUp" ||
          e.key === "ArrowDown" ||
          e.key === "Enter" ||
          e.key === "Escape" ||
          e.key === "Delete"
        )
      ) {
        e.preventDefault();
      }
    }

    // Call the passed onKeyDown handler if it exists
    if (onKeyDown) onKeyDown(e);
  };

  const removeEmojis = (text) => {
    return text.replace(
      /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu,
      "",
    );
  };
  const onHandleChange = (e) => {
    let val = e.target.value;

    if (type == "number" && !allowNegative) {
      val = val.replace(/-/g, "");
      e.target.value = val;
    }

    // Allow partial number inputs like "3.", ".5", "-", or "-3.5" during typing
    const isPartialNumber = /^-?\d*\.?\d*$/.test(val);
    const isLoneMinus = allowNegative && (val === "-" || val === "-.");

    if (type == "number" && val !== "" && !isPartialNumber && !isLoneMinus) {
      // Restore to the last known valid value instead of blanking the field
      e.target.value = value ?? "";
      return;
    }

    let clearValue = removeEmojis(e.target.value);
    e.target.value = clearValue;
    onChange(e);
  };

  return (
    <>
      <TextField
        autoComplete={"off"}
        id={"txt-livechat" + name}
        name={name}
        required={required}
        label={title || ""}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={onHandleChange}
        onBlur={handleOnBlur}
        onKeyDown={handleKeyDown}
        fullWidth={fullWidth}
        type={type === "number" ? "text" : type}
        size={size}
        multiline={multiline}
        minRows={rows}
        maxRows={maxRows}
        variant={variant}
        autoFocus={autoFocus}
        disabled={disabled}
        // color="primary"
        className={className}
        sx={{
          background: disabled
            ? "var(--palette-disabled_button)"
            : "var(--palette-grid_table)",
          position: "relative",
          ...(tooltip && {
            "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
              maxWidth: "calc(100% - 54px)",
            },
          }),
          ...sx,
        }}
        inputProps={{ maxLength: maxLength, ...inputprops }}
        InputLabelProps={{
          style: {
            color: error
              ? "#d32f2f"
              : disabled
                ? "var(--palette-disabled_button_text)"
                : "var(--palette-grey_text)",
          },
          ...InputLabelProps,
        }}
        InputProps={{
          ...inputProps,
          style: {
            // color: "var(--base-140)",
            color: "var(--palette-text)",
            WebkitTextFillColor: disabled ? "var(--palette-text)" : undefined,
            ...inputPropsStyles,
          },
          endAdornment: tooltip ? (
            <InputAdornment
              position="end"
              sx={{
                display: "flex",
                alignItems: "center",
                height: "fill-available !important",
                maxHeight: "fill-available !important;",
              }}
            >
              <CustomWidthTooltip title={tooltip} placement="top">
                <InfoOutlined
                  sx={{ fontSize: 18, color: "var(--palette-text)" }}
                />
              </CustomWidthTooltip>
            </InputAdornment>
          ) : postfix ? (
            <InputAdornment
              position="end"
              sx={{
                color: "var(--palette-text) !important",
                "& p": { color: "var(--palette-text)" },
              }}
            >
              {postfix}
            </InputAdornment>
          ) : null,
          startAdornment: prefix && (
            <InputAdornment
              position="start"
              style={{ color: "var(--palette-text)" }}
            >
              {prefix}
            </InputAdornment>
          ),
          readOnly: readonly,
        }}
        FormHelperTextProps={{
          sx: {
            bgcolor: "var(--palette-grid_table)",
            px: 0.5,
            ...(errorPositionAbsolute && {
              position: "absolute",
              mt: 0,
              bottom: "-14px",
              left: 0,
            }),
            ...helperTextStyles,
            lineHeight: "1 !important",
          },
        }}
        {...(error && { error: !!error, helperText: showErrorText && error })}
        inputRef={inputRef}
      />
    </>
  );
};

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 200,
    background: "var(--palette-stroke)",
    color: "var(--palette-text)",
  },
});

export default UITextField;
